import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { FileValidationStatus } from '@/schema'
import { cn, formatNumber } from '@/utils'

import { useFileUploadFiles, useFileUploadStore } from '../store'

export const FloatingUploadIndicator: React.FC<{
  className?: string
}> = ({ className = '' }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const files = useFileUploadFiles()
  const { isUploading, showFloatingIndicator, uploadRoutePath } =
    useFileUploadStore()

  const isOnUploadPage = location.pathname.includes('/upload')

  const shouldShow = isUploading && !isOnUploadPage && showFloatingIndicator

  // Calculate uploaded vs total files
  const { uploadedCount, totalCount, progressPercentage } = useMemo(() => {
    const uploadingOrUploaded = files.filter(
      (f) =>
        f.validationStatus === FileValidationStatus.UPLOADING ||
        f.validationStatus === FileValidationStatus.UPLOADED
    )
    const uploaded = files.filter(
      (f) => f.validationStatus === FileValidationStatus.UPLOADED
    )
    const percentage =
      uploadingOrUploaded.length > 0
        ? Math.round((uploaded.length / uploadingOrUploaded.length) * 100)
        : 0
    return {
      uploadedCount: uploaded.length,
      totalCount: uploadingOrUploaded.length,
      progressPercentage: percentage,
    }
  }, [files])

  const handleViewClick = () => {
    // Use the stored upload route path (set when UploadPage mounted)
    // This ensures we navigate back to the correct route even if user navigated away
    const routePath = uploadRoutePath || '/deliverable-set/upload' // Fallback to default
    navigate(routePath, { state: { fromFloatingIndicator: true } })
  }

  if (!shouldShow) {
    return null
  }

  return (
    <div
      className={cn(
        'fixed bottom-10 left-1/2 -translate-x-1/2 z-50 bg-white shadow-lg rounded-lg border border-gray-light-200 px-4 py-2',
        className
      )}
    >
      <div className="flex items-center gap-3">
        <div className="relative flex items-center justify-center flex-shrink-0 w-8 h-8">
          <svg className="w-5 h-5 -rotate-90" viewBox="0 0 36 36">
            <circle
              cx="18"
              cy="18"
              r="16"
              fill="none"
              className="stroke-gray-200"
              strokeWidth="4"
            />
            <circle
              cx="18"
              cy="18"
              r="16"
              fill="none"
              className="transition-all duration-300 ease-in-out stroke-brand-600"
              strokeWidth="4"
              strokeDasharray={`${progressPercentage} ${100 - progressPercentage}`}
              strokeLinecap="round"
            />
          </svg>
        </div>

        <span className="text-sm font-semibold text-secondary-700">
          {t('upload.floatingIndicator.uploadingFiles', {
            uploaded: formatNumber(uploadedCount),
            total: formatNumber(totalCount),
            count: totalCount,
          })}
        </span>
        <Button
          variant="text"
          onClick={handleViewClick}
          className="h-auto p-0 text-sm font-semibold text-brand-600 hover:text-brand-700"
        >
          {t('upload.floatingIndicator.view')}
        </Button>
      </div>
    </div>
  )
}
